// node modules
import Vue, { VNode } from 'vue'
import VueI18n from 'vue-i18n'
import VTooltip from 'v-tooltip'
import applyVTooltipLeakPatch from '@/plugins/v-tooltip-leak-patch'
import Toasted from 'vue-toasted'
import PortalVue from 'portal-vue'
import VueCookie from 'vue-cookie'
import Clipboard from 'v-clipboard'
import vClickOutside from 'v-click-outside'
import { VueMasonryPlugin } from 'vue-masonry'
import { library } from '@fortawesome/fontawesome-svg-core'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import VueResource from 'vue-resource'
import ToggleButton from 'vue-js-toggle-button'

// bootstrap components, ONLY import what is needed to reduce size
import { BButton } from 'bootstrap-vue/esm/components/button'
import { BFormInput } from 'bootstrap-vue/esm/components/form-input'
import { BFormFile } from 'bootstrap-vue/esm/components/form-file'
import { BModal } from 'bootstrap-vue/esm/components/modal'
import '@/utils/system/SystemConnector'
import { captureDebugParamIfPresent } from '@/services/onboardingIkeaChecklist'

// FIXME: properly type storybook
// @ts-ignore
import AhaPresenter from '@ahaslides-product/stpancras-storybook-app/src/AhaPresenter'
// import mixpanelUtils from './utils/mixpanel.utils'
import load3rdPartyLibs from '@/services/loaderPlugin'
import i18nPlugin from '@/i18nPlugin'
import i18n from '@/i18n'
import i18nPresenter from '@/i18nPresenter'
import router from '@/router'
import store from '@/vuex/store'
import App from '@/App.vue'
import Config from '@/config'

import mqttSubscribers from '@/mqtt/subscribers'

// 3rd parties
import sentry from '@/services/sentry'
import { createSessionIdFollower, ensureUnleashSession } from '@/services/unleashSession'
import { refreshOn401Interceptor } from '@/services/googleSlidesOAuth'
import { createAhaInterceptor } from '@/services/ahaInterceptor'
import unleashService from '@/services/unleash'

// plugins
import variblesPlugin from '@/plugins/varibles'
import functionsPlugin from '@/plugins/functions'

// filters
import '@/filters'

// Import Amin Template
import '@/assets/scss-v2/styles.scss'
import '@/assets/scss/animate2.css'
import '@fontsource/nunito'
import '@fontsource/plus-jakarta-sans'

import '@fontsource/nunito/600.css'
import './assets/tailwind.css'
import './assets/moveable-custom.css'
import * as iconsMap from './fontAwesome'

// Custom directives
import vHoverIntent from '@/directives/hoverIntent'
import EmitActionDirective from '@/directives/emit-action.directive'
import ClickDirective from '@/directives/click.directive'
import LazyLoadDirective from '@/directives/lazyload'
import PreventWheelPropagation from '@/directives/prevent-wheel-propagation.directive'
import { WebVitalsRecord } from './utils/webVitals'
import bootOfflineServiceWorker from './services/offlineServiceWorkerBoot'
import { installComponentUsage } from './services/componentUsage'

sentry.init()

// Resolve the shared Unleash session anchor once at bootstrap, before any
// general-api call or UnleashClient.start(). Read-then-mint: a value set by the
// pricing page (same root domain) is preserved, so visit order never flips the
// variant. Feeds both the client-side flags (setSessionId) and the
// x-unleash-session request header below.
const unleashSessionId = ensureUnleashSession()
unleashService.setSessionId(unleashSessionId)
// general-api may reset the cookie to the signed-in user's saved id; follow it.
const unleashSessionFollower = createSessionIdFollower(unleashSessionId, {
  onChange: (sessionId) => unleashService.setSessionId(sessionId),
})

// The PowerPoint and Google Slide add-ins run inside cross-origin webviews where
// the x-unleash-session request header triggers a CORS preflight that their
// origins reject. Detect those add-in contexts once so the interceptor can skip
// the header in them; client-side flags still resolve via the session id above.
const isSlideAddinContext = Config.isEnableAddinPowerpoint || Config.integrationPlatform === 'GoogleSlide'

// Capture ?onboardingIkeaChecklistDebug=1 before any router redirects strip it.
// Must run before the Vue app boots so that sessionStorage is set for isChecklistEnabled().
captureDebugParamIfPresent()

// The Microsoft sign-in popup lands on this route with its auth code in the URL, so no tracker may load there.
const isMicrosoftAuthRedirectPage = window.location.pathname === '/microsoft-auth'

// mixpanelUtils.init()
// Because this service is no longer dependent on the Unleash Feature Flag, so I move it here
// And Mixpanel and any other script should be loaded sooner to be able to track the event
if (!isMicrosoftAuthRedirectPage) {
  load3rdPartyLibs()
}
mqttSubscribers.init()

Vue.use(Clipboard)
Vue.use(VueCookie)
Vue.use(VueResource)
Vue.use(VTooltip, {
  defaultOffset: 8,
  defaultTrigger: 'hover click',
})
// Clear v-tooltip's dangling 5s teardown timers so detached DOM is GC'd promptly.
applyVTooltipLeakPatch()
Vue.use(ToggleButton)
Vue.use(VueI18n)
Vue.use(i18nPlugin, { i18nPresenter })
Vue.use(AhaPresenter);
(window as any).AHA_STORYBOOK_VERSION = AhaPresenter.version

Vue.use(vClickOutside)
Vue.use(VueMasonryPlugin)
Vue.use(Toasted, { duration: 5000, singleton: true })
Vue.use(PortalVue)
Vue.component('FontAwesomeIcon', FontAwesomeIcon)
library.add(iconsMap)

// bootstrap components, ONLY import what is needed to reduce size
Vue.component('BButton', BButton)
Vue.component('BFormInput', BFormInput)
Vue.component('BFormFile', BFormFile)
Vue.component('BModal', BModal)

// use custom plugins
Vue.use(variblesPlugin)
Vue.use(functionsPlugin)

// use custom directives
Vue.directive('hover-intent', vHoverIntent)
Vue.directive('lazy-load', LazyLoadDirective)
Vue.directive('aha-emit-action', EmitActionDirective)
Vue.directive('aha-click', ClickDirective)
Vue.directive('prevent-wheel-propagation', PreventWheelPropagation)

installComponentUsage(Vue, Config.componentUsageSampleRate)

Vue.config.productionTip = false
// Listed by name, not /^aha-/: the storybook kit registers its own aha-* Vue components.
Vue.config.ignoredElements = ['aha-background-task', 'aha-background-task-stack']
if (Config.env === 'staging' || Config.env === 'develop') { // enable devtool for staging/ dev
  Vue.config.devtools = true
}

if (Config.env === 'production' && localStorage.getItem('enable_vue_devtools') === 'true') {
  Vue.config.devtools = true
}

// constant value for interceptors
const applicationJson = 'application/json'

Vue.http.interceptors.push(refreshOn401Interceptor)
Vue.http.interceptors.push(createAhaInterceptor(unleashSessionFollower, isSlideAddinContext))

// eslint-disable-next-line import/prefer-default-export
export const app = new Vue({
  i18n,
  router,
  store,
  render: (h): VNode => h(App),
}).$mount('#app')

try {
  const webVitalRecorder = new WebVitalsRecord()
  webVitalRecorder.recordPerfEvents()
} catch (err) {
  sentry.captureException(err)
}

bootOfflineServiceWorker()
